<template>
  <div>
    <el-form ref="form" :model="form" v-loading="isBusy" :rules="rules" label-position="top" label-width="auto">
      <el-form-item label="Owner" v-if="canSelectUser" prop="user_id">
        <el-select
          v-model="form.user_id"
          class="w-100" filterable
          @input="onUserSelected"
          :disabled="isEditMode"
        >
          <el-option
            v-for="item in users"
            :key="item.id"
            :label="item.full_name"
            :value="item.id"
          >
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Brand" prop="brand_id">
        <el-select v-model="form.brand_id" class="w-100" :disabled="isEditMode" @input="onBrandSelected">
          <el-option
            v-for="item in brands"
            :key="item.id"
            :label="item.title"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Email address" prop="email_address_id" v-if="isImportMode">
        <el-select v-model="form.email_address_id" class="w-100" :disabled="isEditMode">
          <el-option
            v-for="item in emailAddresses"
            :key="item.id"
            :label="item.address"
            :disabled="hasNotOutgoingNumbers(item)"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Name" prop="name">
        <el-input v-model="form.name"></el-input>
      </el-form-item>

      <el-form-item v-if="isNormalMode" label="Contacts expire from this group in X days" prop="expiry_period">
        <el-input-number
          v-model="form.expiry_period"
          :precision="0"
          :min="1"
        ></el-input-number>
      </el-form-item>

      <el-form-item label="Import Contacts" prop="contacts_file" v-if="isImportMode">
        <div v-if="isFileSelected">
          <em>{{ this.filename }}</em>
          <span class="btn btn-link btn-link-red px-1" @click="clearFile"><i class="fas fa-times-circle"></i></span>
        </div>
        <file-input v-else v-model="form.file" v-slot:default="{clickHandler}">
                    <span @click.prevent="clickHandler" class="btn btn-link btn-link-purple px-0"><i
                      class="fas fa-folder-open"></i></span>
        </file-input>
      </el-form-item>

      <el-form-item>
        <button class="btn btn-purple" @click.prevent="store">Save</button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import ValidatorMixin from "../../mixins/validator";
import {modes} from "./types";
import FileInput from "../file-input.vue";

export default {
  components: {
    FileInput,
  },

  props: {
    initialModel: {
      type: Object,
      default: function () {
        return {};
      },
    },
    users: {
      type: null,
      default: function () {
        return [];
      },
    },
    storeUrl: {
      type: String,
    },
    canSelectUser: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: modes.normal,
    }
  },

  data() {
    return {
      form: {
        user_id: null,
        brand_id: null,
        name: null,
        expiry_period: null,
        file: null,
        email_address_id: null,
      },
      model: {},
      rules: {
        user_id: [{validator: this.fieldExternalValidator}],
        brand_id: [{validator: this.fieldExternalValidator}],
        name: [{validator: this.fieldExternalValidator}],
        expiry_period: [{validator: this.fieldExternalValidator}],
        email_address_id: [{validator: this.fieldExternalValidator}],
        file: [{validator: this.fieldExternalValidator}],
      },
      errors: {},
      isBusy: false,
    };
  },

  computed: {
    /**
     * @return {boolean}
     */
    isFileSelected() {
      return Boolean(_.get(this.form, 'file', false));
    },

    /**
     * @return {string|null}
     */
    filename() {
      return this.isFileSelected
        ? _.get(this.form, 'file.name')
        : null;
    },

    /**
     * @return {Object|null}
     */
    selectedUser() {
      return _.find(this.users, {id: this.form.user_id}) || null;
    },

    /**
     * @return {Object|null}
     */
    selectedBrand() {
      return _.find(this.brands, {id: this.form.brand_id}) || null;
    },

    /**
     * @return {Array}
     */
    emailAddresses() {
      return _.get(this.selectedBrand, 'email_addresses', []);
    },

    /**
     * @return {Array}
     */
    brands() {
      return _.get(this.selectedUser, 'brands', []);
    },

    /**
     * @return {Boolean}
     */
    isEditMode() {
      return !isNaN(_.get(this.model, 'id'));
    },

    /**
     * @return {String}
     */
    method() {
      return this.isEditMode ? 'PATCH' : 'POST';
    },

    /**
     * @return {Boolean}
     */
    isNormalMode() {
      return this.mode === modes.normal;
    },

    /**
     * @return {boolean}
     */
    isImportMode() {
      return this.mode === modes.import;
    },
  },

  watch: {
    initialModel: {
      handler: function (value) {
        this.model = _.assign({}, this.initialModel);
      },
      immediate: true
    },

    model: {
      handler: function (value) {
        this.initForm(value);
      },
      immediate: true
    },
  },

  methods: {
    onUserSelected() {
      const hasCurrentBrand = _.find(this.brands, {id: this.form.brand_id});
      if (!hasCurrentBrand) {
        this.form.brand_id = null;
      }
    },

    onBrandSelected() {
      this.form.email_address_id = null;
    },

    /**
     * @return {Object}
     */
    getForm() {
      const form = _.assign({}, _.omit(this.form, ['expiry_period']), {
        expiry_period: this.hoursToSeconds(this.form.expiry_period),
        _method: this.method,
      });

      return this.toFormData(form);
    },

    /**
     * @param {object} data
     * @return {FormData}
     */
    toFormData(data) {
      const formData = new FormData();
      _.each(data, (value, name) => {
        formData.append(name, _.isNull(value) || _.isUndefined(value) ? '' : value);
      });

      return formData;
    },

    /**
     * @return {undefined}
     */
    store() {
      this.isBusy = true;
      axios.post(this.storeUrl, this.getForm())
        .then(({data}) => this.storeSuccess(data || {}))
        .catch(({response}) => this.handleErrorResponse(response || {}))
        .finally(() => this.isBusy = false);
    },

    /**
     * @return {undefined}
     */
    storeSuccess({data, redirect, message}) {
      const event = this.isEditMode ? 'updated' : 'created';
      this.model = data;
      this.$emit(event, {model: data, redirect, message});
    },

    /**
     * @param {Object} response
     */
    handleErrorResponse({data: {errors = [], message = null}, status}) {
      if (status === 422 && errors) {
        this.errors = errors;
        this.$refs.form.validate(() => null);
      } else if (message) {
        this.$message.error(message);
      }
    },

    /**
     * @param {Object} model
     */
    initForm(model) {
      this.form = {
        user_id: _.get(model, 'user_id', null),
        brand_id: _.get(model, 'brand_id', null),
        name: _.get(model, 'name', null),
        expiry_period: this.secondsToHours(model.expiry_period),
        email_address_id: _.get(model, 'email_address_id', null),
      };
    },

    /**
     * @param {*} value
     * @return {number|null}
     */
    secondsToHours(value) {
      if (_.isNull(value) || _.isUndefined(value)) {
        return undefined;
      }

      return _.ceil(Number(value) / (60 * 60 * 24));
    },

    /**
     * @param {*} value
     * @return {number|null}
     */
    hoursToSeconds(value) {
      if (_.isNull(value) || _.isUndefined(value)) {
        return null;
      }

      return _.ceil(Number(value)) * (60 * 60 * 24);
    },

    /**
     * @return {undefined}
     */
    clearFile() {
      this.form.file = null;
    },

    /**
     * @param item
     * @return {boolean}
     */
    hasNotOutgoingNumbers(item) {
      return _.isEmpty(_.get(item, 'outgoing_numbers'));
    },
  },

  mixins: [
    ValidatorMixin
  ]
}
</script>
